import { useEffect, useMemo, useState } from "react";
import { academyRequest, createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type {
  TenancyEntity,
  TenancyUnitPage,
  TenancyHistoryPage,
  TenancyHistoryItem,
  UpdateTenancyProfile,
} from "./generated/tenancy";

export const TENANCY_DIRECTORY_CHANGED = "academy-tenancy-directory-changed";

export type TenancyKind = "organization" | "institution";
const routes: Record<TenancyKind, Record<string, string[] | "profile">> = {
  organization: { "people-teams": ["team", "department"], "settings-profile": "profile" },
  institution: {
    "institution-profile": "profile",
    campuses: ["campus"],
    "schools-faculties": ["school", "faculty"],
    departments: ["department"],
    "academic-units": ["academic_unit"],
  },
};
export function isTenancyDirectory(kind: TenancyKind, route: string) {
  return Object.prototype.hasOwnProperty.call(routes[kind], route);
}
export function tenancyFormValues(kind: TenancyKind, profile: TenancyEntity): Record<string, string> {
  return kind === "organization"
    ? {
        "Organization display name": profile.name,
        "Legal name": profile.legalName || "",
        "Primary contact": profile.principalContact || "",
        "Country and time zone": [profile.country, profile.timezone].filter(Boolean).join(" | "),
        "Default language": profile.primaryLanguage || "",
        "Approved change reason": "",
      }
    : {
        "Institution name": profile.name,
        "Institution code": profile.code || "",
        "Legal name": profile.legalName || "",
        "Primary domain": profile.primaryDomain || "",
        "Time zone": profile.timezone || "",
        "Primary language": profile.primaryLanguage || "",
        "Principal contact": profile.principalContact || "",
        "Accreditation reference": "",
        "Approved change reason": "",
      };
}

export function useTenancyDirectory(kind: TenancyKind, route: string) {
  const account = useIdentity();
  const selection = account.access?.memberships.find(m => m.workspaceId === account.session?.workspace.id);
  const entityId = kind === "organization" ? selection?.organizationId : selection?.institutionId;
  const context = account.session
    ? `${account.session.tenantId}:${account.session.user.id}:${account.session.workspace.id}:${entityId || ""}`
    : "";
  const enabled = isTenancyDirectory(kind, route),
    profilePage = routes[kind][route] === "profile";
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const changed = () => setRevision(value => value + 1);
    window.addEventListener(TENANCY_DIRECTORY_CHANGED, changed);
    return () => window.removeEventListener(TENANCY_DIRECTORY_CHANGED, changed);
  }, []);
  const [state, setState] = useState<{
    context: string;
    route: string;
    profile?: TenancyEntity;
    units?: TenancyUnitPage;
    error: string;
  } | null>(null);
  const send = useMemo(() => createCommandSender({ headerOnly: true }), [context]);
  useEffect(() => {
    if (!enabled || !context || !entityId) return;
    let current = true;
    const abort = new AbortController();
    void (async () => {
      try {
        const before = await checkIdentitySession();
        if (
          before.workspace.id !== selection?.workspaceId ||
          before.user.id !== account.session?.user.id ||
          before.tenantId !== account.session?.tenantId
        )
          throw new Error("Workspace changed. Reload to continue.");
        const profile = await academyRequest<TenancyEntity>(
          `tenancy/${kind}/${entityId}`,
          undefined,
          "GET",
          true,
          undefined,
          abort.signal,
        );
        let units: TenancyUnitPage | undefined;
        if (!profilePage) {
          units = { items: [], hasMore: false, nextCursor: null };
          const seen = new Set<string>();
          let cursor: string | null = null;
          do {
            const page: TenancyUnitPage = await academyRequest<TenancyUnitPage>(
              `tenancy/${kind}/${entityId}/units?limit=100${cursor ? `&after=${encodeURIComponent(cursor)}` : ""}`,
              undefined,
              "GET",
              true,
              undefined,
              abort.signal,
            );
            units.items.push(...page.items);
            cursor = page.hasMore ? page.nextCursor : null;
            if (page.hasMore && (!cursor || seen.has(cursor)))
              throw new Error("Unable to continue the directory. Refresh and try again.");
            if (cursor) seen.add(cursor);
          } while (cursor);
          units.items.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
        }
        const after = await checkIdentitySession();
        if (
          before.tenantId !== after.tenantId ||
          before.user.id !== after.user.id ||
          before.workspace.id !== after.workspace.id
        )
          throw new Error("Account or workspace changed. Reload to continue.");
        if (current) setState({ context, route, profile, units, error: "" });
      } catch (error) {
        if (current)
          setState({
            context,
            route,
            error: error instanceof Error ? error.message : "Unable to load tenancy records.",
          });
      }
    })();
    return () => {
      current = false;
      abort.abort();
    };
  }, [context, enabled, entityId, kind, profilePage, revision, route, selection?.workspaceId]);
  const loaded = state?.context === context && state.route === route ? state : null;
  const profile = loaded?.profile;
  const types = routes[kind][route];
  const rows: Array<Record<string, string | number | boolean>> = profilePage
    ? profile
      ? [{ id: profile.id, name: profile.name, status: profile.status, version: profile.version }]
      : []
    : (loaded?.units?.items || [])
        .filter(unit => Array.isArray(types) && types.includes(unit.unitType))
        .map(unit => ({
          id: unit.id,
          name: unit.name,
          owner: unit.leadName || "Not recorded",
          entityName: profile?.name || "",
          scope: unit.parentId
            ? loaded?.units?.items.find(parent => parent.id === unit.parentId)?.name ||
              "Parent outside this view"
            : profile?.name || "",
          parentId: unit.parentId || "",
          unitType: unit.unitType,
          people: unit.memberCount,
          progress: "—",
          status: unit.status,
          version: unit.version,
        }));
  const permission = `tenancy.${kind}.${profilePage ? "profile" : "structure"}.manage`;
  const canManage = !!profile && !!account.session?.permissions.includes(permission);
  async function saveProfile(values: Record<string, string>, reason: string) {
    if (!profile || !entityId) throw new Error("Load the current entity profile before saving.");
    const current = await checkIdentitySession();
    if (
      current.workspace.id !== selection?.workspaceId ||
      current.user.id !== account.session?.user.id ||
      current.tenantId !== account.session?.tenantId
    )
      throw new Error("Workspace changed. Reload before saving.");
    let body: UpdateTenancyProfile;
    if (kind === "organization") {
      const parts = (values["Country and time zone"] || "").split("|").map(value => value.trim());
      if (parts.length > 2)
        throw new Error("Use Country | IANA time zone in the country and time zone field.");
      body = {
        version: profile.version,
        reason,
        name: values["Organization display name"] || "",
        legalName: values["Legal name"] || "",
        country: parts[0] || "",
        timezone: parts[1] || "",
        primaryLanguage: values["Default language"] || "",
        principalContact: values["Primary contact"] || "",
      };
    } else {
      body = {
        version: profile.version,
        reason,
        name: values["Institution name"] || "",
        legalName: values["Legal name"] || "",
        country: profile.country || "",
        code: values["Institution code"] || "",
        primaryDomain: values["Primary domain"] || "",
        timezone: values["Time zone"] || "",
        primaryLanguage: values["Primary language"] || "",
        principalContact: values["Principal contact"] || "",
      };
    }
    await send(`tenancy/${kind}/${entityId}/commands/update-profile`, body);
    setRevision(value => value + 1);
  }
  return {
    enabled,
    profilePage,
    context,
    profile,
    rows,
    canManage,
    saveProfile,
    loading: enabled && !!entityId && !loaded,
    error: enabled
      ? loaded?.error ||
        account.error ||
        (!account.loading && !entityId ? "No current entity membership is available for this workspace." : "")
      : "",
    refresh: () => setRevision(value => value + 1),
  };
}
export type TenancyDirectory = ReturnType<typeof useTenancyDirectory>;

/** Reads the original detail drawer's history; IDs are intent and reauthorized on every page. */
export function useTenancyHistory(kind: TenancyKind, route: string, resourceId?: string) {
  const account = useIdentity();
  const membership = account.access?.memberships.find(m => m.workspaceId === account.session?.workspace.id);
  const entityId = kind === "organization" ? membership?.organizationId : membership?.institutionId;
  const memberPage = route === (kind === "organization" ? "people-members" : "people-directory");
  const enabled = !!resourceId && (isTenancyDirectory(kind, route) || memberPage);
  const resource = memberPage ? "membership" : routes[kind][route] === "profile" ? "entity" : "unit";
  const context = enabled
    ? `${account.session?.tenantId}:${account.session?.user.id}:${membership?.workspaceId}:${entityId}:${resourceId}`
    : "";
  const [revision, refresh] = useState(0);
  const [state, setState] = useState<{ context: string; items: TenancyHistoryItem[]; error: string } | null>(
    null,
  );
  useEffect(() => {
    if (!enabled || !entityId || !resourceId) return;
    let current = true;
    const abort = new AbortController();
    void (async () => {
      try {
        const beforeSession = await checkIdentitySession();
        if (
          beforeSession.workspace.id !== membership?.workspaceId ||
          beforeSession.user.id !== account.session?.user.id ||
          beforeSession.tenantId !== account.session?.tenantId
        )
          throw new Error("Workspace changed. Reload to view history.");
        const items: TenancyHistoryItem[] = [];
        let before: number | null = null;
        do {
          const page: TenancyHistoryPage = await academyRequest(
            `tenancy/${kind}/${entityId}/history/${resource}/${resourceId}${before ? `?before=${before}` : ""}`,
            undefined,
            "GET",
            true,
            undefined,
            abort.signal,
          );
          items.push(...page.items);
          if (
            page.nextVersion !== null &&
            (page.nextVersion < 1 || (before !== null && page.nextVersion >= before))
          )
            throw new Error("History pagination did not advance.");
          before = page.nextVersion;
        } while (before !== null);
        const after = await checkIdentitySession();
        if (
          beforeSession.tenantId !== after.tenantId ||
          beforeSession.user.id !== after.user.id ||
          beforeSession.workspace.id !== after.workspace.id
        )
          throw new Error("Account context changed. Reload to view history.");
        if (current) setState({ context, items, error: "" });
      } catch (error) {
        if (current)
          setState({
            context,
            items: [],
            error: error instanceof Error ? error.message : "History unavailable.",
          });
      }
    })();
    return () => {
      current = false;
      abort.abort();
    };
  }, [context, enabled, entityId, kind, membership?.workspaceId, resource, resourceId, revision]);
  const loaded = state?.context === context ? state : null;
  return {
    enabled,
    items: loaded?.items || [],
    error: loaded?.error || (!entityId ? "Current entity context is unavailable." : ""),
    loading: enabled && !loaded,
    refresh: () => refresh(v => v + 1),
  };
}
